<script lang="ts">
  import isMobile from 'is-mobile';
  import Button from 'Catalyst/components/Button/Button.svelte';
  import {createEventDispatcher, onMount} from 'svelte';
  import CatalystApp from 'Catalyst/components/CatalystApp/CatalystApp.svelte';
  import AppleIcon from './icons/AppleIcon.svelte';
  import FacebookIcon from './icons/FacebookIcon.svelte';
  import GoogleIcon from './icons/GoogleIcon.svelte';
  import authManager from 'Utility/authManager';
  import {getEnvironmentVariable} from 'Utility/document';
  import {personalInfo} from 'Store/InvestorFlowStore';
  import {getContext} from 'svelte';
  import type {BrandColor} from 'Catalyst/utility/theme';
  import type {Writable} from 'svelte/store';
  import {getGapClass} from 'Catalyst/components/ThemeContext';

  export let hostURL: string = window.location.origin;
  export let isEmbedded: boolean = false;
  export let experimentUniqueId: string = '';

  const dispatch = createEventDispatcher();
  const brandColors = getContext<Writable<BrandColor[]>>('brandColors');

  const tokenHandler = (event: MessageEvent) => {
    if (
      event?.data?.type === 'social_login_popup_response' &&
      event?.data?.body
    ) {
      personalInfo.update(prev => ({
        ...prev,
        email: event.data.body.email || '',
        email_confirmation: event.data.body.email || '',
        name: event.data.body.first_name + ' ' + event.data.body.last_name,
        first_name: event.data.body.first_name || '',
        last_name: event.data.body.last_name || '',
      }));
      if (event.data.body.access_token) {
        authManager.bindAccessToken(event.data.body.access_token);
      }
      dispatch('submit');
    }
  };

  const authProviders = [
    {
      icon: GoogleIcon,
      provider: 'google',
      authUrl: `${hostURL}/users/auth/google_oauth2?popup=true&embedded=${isEmbedded}&experimentUniqueId=${experimentUniqueId}`,
    },
    {
      icon: AppleIcon,
      provider: 'apple',
      authUrl: `${hostURL}/users/auth/apple?popup=true&embedded=${isEmbedded}&experimentUniqueId=${experimentUniqueId}`,
    },
    {
      icon: FacebookIcon,
      provider: 'facebook',
      authUrl: `${hostURL}/users/auth/facebook?popup=true&embedded=${isEmbedded}&experimentUniqueId=${experimentUniqueId}`,
    },
  ];

  const formRefs = {};

  const onSubmit = async (provider: string) => {
    const emailHost = getEnvironmentVariable('email_host');

    if (provider === 'facebook' && isMobile() && emailHost) {
      const brandColor = brandColors ? $brandColors[0]?.value : undefined;

      const url = new URL(
        `${emailHost}/users/auth/facebook_login_confirmation`
      );
      url.searchParams.append('hostURL', hostURL);
      url.searchParams.append('isEmbedded', String(isEmbedded));
      url.searchParams.append('omniauthOrigin', window.location.origin);
      url.searchParams.append('experimentUniqueId', experimentUniqueId);

      if (brandColor) {
        url.searchParams.append('brandColor', brandColor);
      }

      window.open(
        url.toString(),
        'facebook_login_confirmation',
        'width=500,height=600'
      );
    } else {
      window.open('about:blank', 'login', 'width=500,height=600');
      formRefs[provider].setAttribute('target', 'login');
      formRefs[provider].submit();
    }
  };

  onMount(() => {
    window.addEventListener('message', tokenHandler, false);
    return () => {
      window.removeEventListener('message', tokenHandler, false);
    };
  });

  $: gapClass = getGapClass();
</script>

<CatalystApp>
  <div class="flex flex-col {gapClass}">
    {#each authProviders as provider}
      <form
        class="w-full flex flex-col"
        bind:this={formRefs[provider.provider]}
        action={provider.authUrl}
        method="post"
        on:submit|preventDefault={() => onSubmit(provider.provider)}
      >
        <Button style="tonal" type="submit" size="lg" on:click>
          <span>Continue with</span>
          <svelte:component this={provider.icon} />
        </Button>
      </form>
    {/each}
  </div>
</CatalystApp>
